import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Grid,
  Collapse,
  IconButton,
  Paper
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import LoglineGenerator from '../components/story-tools/LoglineGenerator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayCircleFilledRoundedIcon from '@mui/icons-material/PlayCircleFilledRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';

function Landing() {
  const theme = useTheme();
  const [openGenerator, setOpenGenerator] = useState(false);

  // Set page title
  useEffect(() => {
    document.title = 'Luminara';
  }, []);

  // Do not redirect logged-in users; allow trying the generator on Landing

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: `radial-gradient(circle at 20% 20%, ${alpha(theme.palette.primary.main, 0.15)} 0%, transparent 40%),
                     radial-gradient(circle at 80% 0%, ${alpha(theme.palette.secondary.main, 0.15)} 0%, transparent 40%)`,
        py: 8
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h2" fontWeight={800} gutterBottom>
              Luminara — Film & TV Project Management
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
              Plan productions, manage casts, schedules, and resources — all in one modern workspace.
              <Button
                component={RouterLink}
                to="/features"
                size="small"
                sx={{
                  ml: 2,
                  borderRadius: 999,
                  px: 2.5,
                  py: 0.5,
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08)
                  }
                }}
              >
                Explore features
              </Button>
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                component={RouterLink}
                to="/register"
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIosRoundedIcon sx={{ fontSize: 16 }} />}
                sx={{
                  borderRadius: 3,
                  px: { xs: 5, md: 6 },
                  py: { xs: 1.75, md: 2 },
                  textTransform: 'none',
                  fontSize: '1.05rem',
                  letterSpacing: 0.5,
                  fontWeight: 600,
                  background: 'linear-gradient(120deg, #00c6ff 0%, #0072ff 60%, #0047b3 100%)',
                  boxShadow: '0 12px 40px rgba(0, 114, 255, 0.45)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  animation: 'pulse 4s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%': { boxShadow: '0 12px 40px rgba(0, 114, 255, 0.45)' },
                    '50%': { boxShadow: '0 20px 60px rgba(0, 114, 255, 0.55)' },
                    '100%': { boxShadow: '0 12px 40px rgba(0, 114, 255, 0.45)' }
                  },
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 20px 60px rgba(0, 114, 255, 0.65)'
                  }
                }}
              >
                Start for Free
              </Button>
              <Button
                component={RouterLink}
                to="/login"
                variant="outlined"
                size="large"
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  borderColor: alpha(theme.palette.divider, 0.2)
                }}
              >
                Sign In
              </Button>
              <Button
                component={RouterLink}
                to="/pricing"
                variant="text"
                size="large"
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08)
                  }
                }}
              >
                View Pricing
              </Button>
              <Button
                component={RouterLink}
                to="/tutorials"
                variant="text"
                size="large"
                startIcon={<PlayCircleFilledRoundedIcon />}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08)
                  }
                }}
              >
                Watch Tutorials
              </Button>
            </Stack>
          </Grid>
          
        </Grid>
        {/* Public preview: Logline Generator (collapsible) */}
        <Box sx={{ mt: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Try the Logline Generator
            </Typography>
            <Button
              size="small"
              variant={openGenerator ? 'outlined' : 'contained'}
              color="primary"
              onClick={() => setOpenGenerator(v => !v)}
              startIcon={<ExpandMoreIcon sx={{ transform: openGenerator ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />}
              sx={{ textTransform: 'none' }}
            >
              {openGenerator ? 'Collapse' : 'Expand to get started'}
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Expand to try Simple or sample AI without signing in. Demo results aren’t saved and clear on refresh.
          </Typography>
          {!openGenerator && (
            <Paper
              variant="outlined"
              onClick={() => setOpenGenerator(true)}
              sx={{
                p: 2,
                borderStyle: 'dashed',
                borderRadius: 2,
                cursor: 'pointer',
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(144,202,249,0.06)' : 'rgba(25,118,210,0.04)',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(144,202,249,0.1)' : 'rgba(25,118,210,0.08)'
                }
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Get started
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Generate sample AI loglines instantly (no sign-in needed).
              </Typography>
            </Paper>
          )}
          <Collapse in={openGenerator} timeout="auto" unmountOnExit>
            <LoglineGenerator projectId="landing" />
          </Collapse>
        </Box>
      </Container>
    </Box>
  );
}

export default Landing;
